<template>
  <div class="withdraw" data-testid="withdraw">
    <section class="withdraw__header">
      <Row class="withdraw__header--row" :has-line="false">
        <template #top-left>
          <BaseText size="p4" :color="NebraskaColors.elementSecondary">
            Disponível para resgate
          </BaseText>
        </template>
        <template #top-right>
          <BaseText size="p4" :color="NebraskaColors.elementPrimary">
            {{ withdrawDetails.totalBalance.formatted }}
          </BaseText>
        </template>
      </Row>
    </section>
    <section class="withdraw__main">
      <HeadingDisplay
        title="Defina o valor a resgatar"
        :subtitle="portfolioName"
      />
      <SingleInputMoney
        class="withdraw__single-input-money"
        :shortcut-value="withdrawDetails.buttons"
        has-shortcut
        :status="status"
        :helper-text="helperText"
        @change="onChangeValue"
      />
      <div class="withdraw__list">
        <ListDisplay
          v-for="(detail, index) in listDetails"
          :key="index"
          class="withdraw__list--item"
          :has-divider="index !== listDetails.length - 1"
          :title="detail.title"
          :value="detail.value"
        />
      </div>
    </section>
    <ButtonContainer class="withdraw__button-container">
      <template #button-primary>
        <ButtonPrimary
          label="Continuar"
          size="large"
          :disabled="isDisabled"
          @click="withdrawClick"
        />
      </template>
    </ButtonContainer>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, inject, Ref } from 'vue';

import {
  Row,
  HeadingDisplay,
  BaseText,
  ListDisplay,
  ButtonContainer,
  ButtonPrimary
} from '@warrenbrasil/nebraska-web';
import { NebraskaColors } from '@warrenbrasil/nebraska-tokens-web';
import { SingleInputMoney } from '@/modules/common/components/SingleInputMoney';

import { formatCurrency } from '@/modules/common/helpers/currency';

import { IWithdrawDetails } from '@/modules/transaction/services/variable-income';

const changeAmountValue = inject('changeAmountValue', (value: number) => ({
  value
}));

const withdrawDetails = inject('withdrawDetails') as Ref<IWithdrawDetails>;

const amountValue = ref(0);

const isDisabled = computed(() => {
  return amountValue.value <= 0 || status.value === 'error';
});

const portfolioName = computed(() => {
  return withdrawDetails.value?.portfolioName ?? '';
});

const minimumRedemption = computed(() => {
  return withdrawDetails.value.minimumRedemption.value;
});

const totalBalance = computed(() => {
  return withdrawDetails.value.totalBalance.value;
});

const restValue = computed(() => {
  return totalBalance.value - amountValue.value;
});

const maximumRedemption = computed(() => {
  return formatCurrency(
    totalBalance.value - withdrawDetails.value.minimumStayValue.value
  );
});

const status = computed(() => {
  return (amountValue.value < minimumRedemption.value &&
    amountValue.value !== 0) ||
    amountValue.value > totalBalance.value ||
    (restValue.value > 0 &&
      restValue.value < withdrawDetails.value.minimumStayValue.value)
    ? 'error'
    : '';
});

const helperText = computed(() => {
  if (amountValue.value < minimumRedemption.value && amountValue.value !== 0)
    return `O valor mínimo de resgate é de ${withdrawDetails.value.minimumRedemption.formatted}. Escolha um valor maior ou igual para resgatar.`;
  if (amountValue.value > totalBalance.value)
    return `O saldo desta carteira é de ${withdrawDetails.value.totalBalance.formatted}. Escolha um valor menor ou igual para resgatar.`;
  if (
    restValue.value > 0 &&
    restValue.value < withdrawDetails.value.minimumStayValue.value
  )
    return `O valor mínimo de permanência é de ${withdrawDetails.value.minimumStayValue.formatted}. Escolha um valor inferior a R$ ${maximumRedemption.value} ou solicite o resgate total.`;

  return '';
});

const listDetails = computed(() => {
  const list = [
    {
      title: 'Mínimo para resgate',
      value: withdrawDetails.value.minimumRedemption.formatted,
      show: Boolean(withdrawDetails.value.minimumRedemption)
    },
    {
      title: 'Mínimo de permanência',
      value: withdrawDetails.value.minimumStayValue.formatted,
      show: Boolean(withdrawDetails.value.minimumStayValue)
    },
    {
      title: 'Prazo total de resgate',
      value: withdrawDetails.value.totalRedemptionDays,
      show: Boolean(withdrawDetails.value.totalRedemptionDays)
    }
  ];

  return list.filter(item => item.show);
});

function withdrawClick() {
  changeAmountValue(amountValue.value);
}

function onChangeValue(data: { value: number }) {
  amountValue.value = data.value;
}
</script>

<style lang="less" scoped>
.withdraw {
  &__header {
    background-color: @background-tertiary;
    height: 64px;
    display: flex;
    justify-content: center;
    align-items: center;
    padding: 0 @size-spacing-x500;

    &--row {
      max-width: 492px;
      width: 100%;
      background-color: transparent;
      padding: 0;
    }
  }

  &__main {
    max-width: 524px;
    margin: 0 auto;
    padding: @size-spacing-x1000 @size-spacing-x400 @size-spacing-x1800;
  }

  &__single-input-money {
    margin-top: @size-spacing-x600;
  }

  &__list {
    margin-top: @size-spacing-x800;

    &--item {
      margin-bottom: @size-spacing-x450;
      padding-bottom: @size-spacing-x450;
    }
  }

  &__button-container {
    background-color: @background-secondary;
    width: 100%;
    position: fixed;
    bottom: 0;
    left: 0;
    z-index: 20;
  }
}
</style>
